import { ChangeDetectionStrategy, Component, ContentChild, inject, input } from '@angular/core';
import { ExpansionPanelHeaderComponent } from './expansion-panel-header';
import { ExpansionPanelContentComponent } from './expansion-panel-content';
import { delay } from 'rxjs';
import { AccordionState } from './accordion.state';
import { AccordionComponent } from './accordion';
import { animate, AUTO_STYLE, state, style, transition, trigger } from '@angular/animations';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { AsyncPipe } from '@angular/common';

const DEFAULT_DURATION = 300;

@Component({
  selector: 'ava-expansion-panel',
  standalone: true,
  template: `
    @let selectedPanelId = selectedPanelId$ | async;
    @let isSelectedPanelId = panelId() === selectedPanelId;

    <button
      [class.tw-border-b-transparent]="isSelectedPanelId"
      (click)="clicked(panelId())"
      [attr.id]="panelId()"
      class="tw-flex tw-h-12 tw-flex-1 tw-cursor-pointer tw-select-none tw-items-baseline tw-border-b-2 tw-border-gray-600 !tw-px-4 !tw-py-2 tw-text-start tw-text-blue-dark"
    >
      <i class="tw-text-xs" nz-icon [nzType]="isSelectedPanelId ? 'down' : 'right'" theme="outline"></i>
      <span class="!tw-mx-4 tw-flex-1 tw-whitespace-nowrap sm:tw-text-2xl -sm:tw-text-xl">
        <ng-content select="ava-expansion-panel-header" />
      </span>
      <i nz-icon [nzType]="isSelectedPanelId ? 'minus' : 'plus'" theme="outline"></i>
    </button>

    <div
      [@collapse]="!isSelectedPanelId"
      (@collapse.done)="isSelectedPanelId ? onCollapseDone() : null"
      class="tw-block tw-overflow-hidden tw-pb-2 tw-pl-2 tw-pr-2"
    >
      <ng-content select="ava-expansion-panel-content" />
    </div>
  `,
  host: {
    class: 'tw-flex tw-flex-col',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('collapse', [
      state('false', style({ height: AUTO_STYLE, visibility: AUTO_STYLE })),
      state('true', style({ height: '0', visibility: 'hidden' })),
      transition('false => true', animate(DEFAULT_DURATION + 'ms ease-in')),
      transition('true => false', animate(DEFAULT_DURATION + 'ms ease-out')),
    ]),
  ],
  imports: [NzIconDirective, AsyncPipe],
})
export class ExpansionPanelComponent {
  state = inject(AccordionState);
  private accordion = inject(AccordionComponent);
  @ContentChild(ExpansionPanelHeaderComponent, { read: ExpansionPanelHeaderComponent })
  header: ExpansionPanelHeaderComponent | null = null;
  @ContentChild(ExpansionPanelContentComponent, { read: ExpansionPanelContentComponent })
  content: ExpansionPanelContentComponent | null = null;
  panelId = input('');

  selectedPanelId$ = this.state.selectedPanelId$.pipe(delay(0));

  clicked(panelId: string) {
    this.state.userClicked.next(panelId);
  }

  onCollapseDone() {
    this.accordion.onPanelExpanded();
  }
}
